import { MRT_ColumnDef, useMaterialReactTable } from 'material-react-table';
import { Box, IconButton, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Button from 'src/presentations/components/atom/button';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { InvoiceChargeDomainMappingDto, UpsertInvoiceChargeDomainMappingReq } from 'src/generated/services/TFinancialApi';
import {
  InvoiceChargeDomainMappingFilters,
  useDeleteInvoiceChargeDomainMappings,
  useInvoiceChargeDomainMappingMetadata,
  useInvoiceChargeDomainMappings,
  useUpsertInvoiceChargeDomainMapping,
} from 'src/use-cases/settings/useInvoiceToCharge';

import { useEnhancedTable } from 'src/presentations/hooks/useEnhancedTable';
import { useTableEntityHandler } from 'src/presentations/hooks/useTableEntityHandler';
import { SettingsBaseTable } from 'src/presentations/components/molecules/settings/table/BaseTable';
import { createTableConfig } from 'src/presentations/utils/tableConfigUtils';
import { useGetAllServiceProviders } from 'src/use-cases/serviceProvider/useServiceProvider.ts';
import { createFilterItems } from 'src/presentations/components/molecules/table/columnHeader';
import { SettingsColumnHeader } from '../SettingsColumnHeader';
import { SettingsMain } from '../SettingsMain';

const firstCellStyles = {
  boxShadow: '4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
  borderLeft: '1px solid #E5E7EB',
  width: '100px',
  padding: '0 10px 0 10px !important',

  '& .MuiBox-root': {
    gap: '0!important',
    width: '90px',
    justifyContent: 'center',

    '& svg': {
      width: '20px!important',
    },
  },
};

// Map UI column identifiers to backend sorting field names
const SORTING_FIELD_MAP: Record<string, string> = {
  serviceProviderId: 'sp.code',
} as const;

interface InvoiceChargeTableProps {
  searchQuery?: string;
}

export const InvoiceChargeMain: FC<InvoiceChargeTableProps> = (props): JSX.Element => {
  const { searchQuery: externalSearchQuery = '' } = props;
  const [filters, setFilters] = useState<InvoiceChargeDomainMappingFilters>({});

  // Track whether this is the initial load
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Use the enhanced table hook that combines pagination, sorting, and validation
  const { pagination, sorting, handleSortingChange, onChangePage, onChangePageSize, getTextFieldProps, validateItem, clearValidationErrors, getRequestParams } =
    useEnhancedTable<InvoiceChargeDomainMappingDto>({
      initialSorting: [],
      onSearchQueryChange: externalSearchQuery,
      requiredFields: ['invoiceChargeName', 'chargeDomain', 'chargeSubDomain', 'legType', 'contractChargeUnit'],
    });

  // API hooks
  const { mutateAsync: upsertMapping } = useUpsertInvoiceChargeDomainMapping();
  const { mutateAsync: deleteMapping } = useDeleteInvoiceChargeDomainMappings();

  // Fetch invoice charge domain mappings data with pagination and sorting
  // We're now using the getRequestParams method from the enhanced table hook
  const requestParams = useMemo(() => {
    const params = getRequestParams();

    // Map the sorting field from UI column name to backend field name
    if (params.sortBy && SORTING_FIELD_MAP[params.sortBy]) {
      return {
        ...params,
        sortBy: SORTING_FIELD_MAP[params.sortBy], // Use the backend field name for sorting
      };
    }

    return params;
  }, [getRequestParams]);

  const { data, isLoading, refetch } = useInvoiceChargeDomainMappings(requestParams, filters);

  // Get metadata for filter options with current filters
  const { data: metadataData, refetch: refetchMetadata } = useInvoiceChargeDomainMappingMetadata(filters);

  // Refetch data when filters change
  useEffect(() => {
    if (isInitialLoad) {
      // On initial load, don't trigger a refetch with empty filters
      setIsInitialLoad(false);
    } else {
      // For subsequent filter changes, refetch data
      refetch();
    }
  }, [filters, refetch, isInitialLoad, setIsInitialLoad]);

  // Extract data from API response
  const mappings = useMemo(() => data?.data?.content || [], [data?.data?.content]);
  const totalMappings = data?.data?.totalElements || 0;

  // Calculate pagination metadata in the frontend
  const totalPages = Math.ceil(totalMappings / pagination.pageSize);
  const isLastPage = pagination.pageIndex >= totalPages - 1;
  const currentPageNumber = pagination.pageIndex + 1;

  const { data: serviceProviders, isLoading: isLoadingServiceProviders } = useGetAllServiceProviders();
  const serviceProviderOptions = useMemo(
    () =>
      serviceProviders?.data.map((item) => ({
        label: item.code,
        value: item.id,
      })) || [],
    [serviceProviders]
  );

  // Update a specific filter field
  const updateFilter = useCallback(
    <K extends keyof InvoiceChargeDomainMappingFilters>(key: K, value: InvoiceChargeDomainMappingFilters[K]) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [setFilters]
  );

  // Handler for table header sorting
  const handleTableSort = useCallback(
    (columnId: string, direction: 'asc' | 'desc') => {
      handleSortingChange([{ id: columnId, desc: direction === 'desc' }]);
    },
    [handleSortingChange]
  );

  // Handler for table filters
  const handleTableFilter = useCallback(
    (columnId: string, selectedValues: string[], allOptions?: { id: string; label: string; count: number }[]) => {
      // If all options are selected or none are selected, clear the filter
      const isAllSelected = allOptions && selectedValues.length === allOptions.length;

      if (!selectedValues.length || isAllSelected) {
        // Clear the filter for this column
        switch (columnId) {
          case 'serviceProviderId':
            updateFilter('serviceProviderCodes', undefined);
            break;
          case 'invoiceChargeName':
            updateFilter('invoiceChargeNames', undefined);
            break;
          case 'chargeDomain':
            updateFilter('chargeDomains', undefined);
            break;
          case 'chargeSubDomain':
            updateFilter('chargeSubDomains', undefined);
            break;
          case 'legType':
            updateFilter('legTypes', undefined);
            break;
          case 'contractChargeUnit':
            updateFilter('contractChargeUnits', undefined);
            break;
          default:
            break;
        }

        // Critical: Always refresh metadata and data after clearing filters
        refetchMetadata();
        refetch();
        return;
      }

      // Apply the filter based on column ID
      switch (columnId) {
        case 'serviceProviderId':
          updateFilter('serviceProviderCodes', selectedValues);
          break;
        case 'invoiceChargeName':
          updateFilter('invoiceChargeNames', selectedValues);
          break;
        case 'chargeDomain':
          updateFilter('chargeDomains', selectedValues);
          break;
        case 'chargeSubDomain':
          updateFilter('chargeSubDomains', selectedValues);
          break;
        case 'legType':
          updateFilter('legTypes', selectedValues);
          break;
        case 'contractChargeUnit':
          updateFilter('contractChargeUnits', selectedValues);
          break;
        default:
          break;
      }
    },
    [updateFilter, refetchMetadata, refetch]
  );

  const columns = useMemo(
    (): MRT_ColumnDef<InvoiceChargeDomainMappingDto>[] => [
      {
        accessorKey: 'serviceProviderId',
        header: 'Service Provider',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.serviceProviderCodes);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.serviceProviderCodes}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        editVariant: 'select',
        editSelectOptions: serviceProviderOptions,
        Cell: ({ cell }) => {
          // Display the code (label) instead of the id (value)
          const serviceProvider = serviceProviderOptions.find((entity) => entity.value === cell.getValue());
          return serviceProvider ? serviceProvider.label : '-';
        },
      },
      {
        accessorKey: 'invoiceChargeName',
        header: 'Invoice Charge Name',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.invoiceChargeNames);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.invoiceChargeNames}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('invoiceChargeName'),
      },
      {
        accessorKey: 'chargeDomain',
        header: 'Charge Domain',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.chargeDomains);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.chargeDomains}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('chargeDomain'),
      },
      {
        accessorKey: 'chargeSubDomain',
        header: 'Charge Sub Domain',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.chargeSubDomains);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.chargeSubDomains}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('chargeSubDomain'),
      },
      {
        accessorKey: 'legType',
        header: 'Leg Type',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.legTypes);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.legTypes}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('legType'),
      },
      {
        accessorKey: 'contractChargeUnit',
        header: 'Charge Unit',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.contractChargeUnits);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.contractChargeUnits}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleTableFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('contractChargeUnit'),
      },
    ],
    [
      getTextFieldProps,
      serviceProviderOptions,
      handleTableSort,
      handleTableFilter,
      metadataData?.data?.serviceProviderCodes,
      metadataData?.data?.invoiceChargeNames,
      metadataData?.data?.chargeDomains,
      metadataData?.data?.chargeSubDomains,
      metadataData?.data?.legTypes,
      metadataData?.data?.contractChargeUnits,
      filters.serviceProviderCodes,
      filters.invoiceChargeNames,
      filters.chargeDomains,
      filters.chargeSubDomains,
      filters.legTypes,
      filters.contractChargeUnits,
    ]
  );

  // Prepare entity for upsert (standardized format)
  const prepareEntityForUpsert = useCallback(
    (values: any, originalData?: any): UpsertInvoiceChargeDomainMappingReq => ({
      ...(originalData ? { id: originalData.id } : {}),
      invoiceChargeName: values.invoiceChargeName,
      chargeDomain: values.chargeDomain,
      chargeSubDomain: values.chargeSubDomain,
      legType: values.legType,
      contractChargeUnit: values.contractChargeUnit,
      serviceProviderId: Number(values.serviceProviderId) || 0,
    }),
    []
  );

  // Use the standardized entity handler hook
  const {
    handleCreate: handleCreateMapping,
    handleUpdate: handleUpdateMapping,
    handleDelete: handleDeleteMapping,
  } = useTableEntityHandler<UpsertInvoiceChargeDomainMappingReq>(
    {
      entityName: 'Invoice charge mapping',
      upsertEntity: upsertMapping,
      deleteEntity: deleteMapping,
      prepareEntityForUpsert,
    },
    validateItem
  );

  // Create a wrapper for delete that passes the correct options for IDs array
  const handleDeleteMappingWithOptions = useCallback(
    (row: any) =>
      // Pass useIdsArray option to ensure the delete function uses the correct format
      handleDeleteMapping(row, {
        useIdsArray: true,
        additionalParams: {},
      }),
    [handleDeleteMapping]
  );

  // Render row actions for invoice charges
  const renderRowActions = useCallback(
    ({ row, table }) => (
      <Box sx={{ display: 'flex' }}>
        <Tooltip title='Edit'>
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton color='error' onClick={() => handleDeleteMappingWithOptions(row)}>
            <DeleteIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    [handleDeleteMappingWithOptions]
  );

  // Create the table instance
  const baseTableConfig = createTableConfig<InvoiceChargeDomainMappingDto>({
    onCreatingCancel: clearValidationErrors,
    onEditingCancel: clearValidationErrors,
  });

  const table = useMaterialReactTable({
    ...baseTableConfig,
    onCreatingRowSave: handleCreateMapping,
    onEditingRowSave: handleUpdateMapping,
    data: mappings,
    columns,
    state: {
      sorting,
      isLoading,
    },
    enableEditing: true,
    createDisplayMode: 'row',
    editDisplayMode: 'row',
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
      },
    },
    muiTableHeadProps: {
      sx: {
        '& th:first-child': firstCellStyles,
      },
    },
    muiTableBodyProps: {
      sx: {
        '& td:first-child': firstCellStyles,
      },
    },
    onSortingChange: (updatedSorting) => {
      if (typeof updatedSorting === 'function') {
        const newSortingState = updatedSorting(sorting);
        handleSortingChange(newSortingState);
      } else {
        handleSortingChange(updatedSorting);
      }
    },
    renderRowActions: ({ row, table }) => renderRowActions({ row, table }),
  });

  return (
    <SettingsMain
      title='Charge Mappings'
      subtitle='Adjust the invoice to charge domain mappings'
      backPath='/settings'
      topActions={
        <Button
          variant='primary'
          className='!pr-12 !pl-16 !ml-12'
          onClick={() => {
            table.setCreatingRow(true);
          }}
        >
          <div className='flex items-center gap-10'>
            <span className='text-14 xl:text-16'>Add New</span>
            <AddIcon />
          </div>
        </Button>
      }
    >
      <SettingsBaseTable table={table} totalItems={totalMappings} pagination={pagination} onChangePage={onChangePage} onChangePageSize={onChangePageSize} />
    </SettingsMain>
  );
};
